import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, functions } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import SeoMeta from '../../components/marketing/SeoMeta';
import { getMissingExternalAttendeeProfileFields } from '../../utils/externalAttendeeProfile';
import { buildChildAccountSetupPath } from '../../utils/childAccountReturn';
import './publicEvents.css';

const listPublicEventsFn = httpsCallable(functions, 'listPublicEvents');
const checkoutFn = httpsCallable(functions, 'createEventCheckoutCallable');
const getMyBookingFn = httpsCallable(functions, 'getMyExternalBooking');
const updateMyBookingFn = httpsCallable(functions, 'updateMyExternalBooking');
const joinEventWaitlistFn = httpsCallable(functions, 'joinEventWaitlist');
const withdrawEventWaitlistFn = httpsCallable(functions, 'withdrawEventWaitlist');
const listPublicCoursesFn = httpsCallable(functions, 'listPublicCourses');
const getCourseFormFn = httpsCallable(functions, 'getPublicCourseBookingForm');
const courseCheckoutFn = httpsCallable(functions, 'createGenericCourseCheckout');
const managePendingCourseCheckoutFn = httpsCallable(functions, 'managePendingGenericCourseCheckout');
const joinCourseWaitlistFn = httpsCallable(functions, 'joinCourseWaitlist');
const getLinkedDocumentFn = httpsCallable(functions, 'getLinkedDocumentAccess');

function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatMoney(minor, currency) {
  if (minor === null || minor === undefined || !Number.isFinite(Number(minor))) return 'Price unavailable';
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'GBP',
  });
  const decimals = formatter.resolvedOptions().maximumFractionDigits || 0;
  return formatter.format(Number(minor) / Math.pow(10, decimals));
}

function formatWhen(start, options = {}) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: options.year ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(start));
}

function formatCourseDate(start, options = {}) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: options.weekday ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: options.year ? 'numeric' : undefined,
  }).format(new Date(start));
}

function formatCourseTime(start) {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(start));
}

function formatDate(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00`));
}

function coverageCopy(event) {
  if (!event.recurring)
    return {
      label: 'One event',
      detail: `This ticket is for the event on ${formatWhen(event.start, { year: true })}.`,
    };
  if (event.ticketPaymentBasis === 'whole_series')
    return {
      label: 'Remaining series',
      detail: `Pay once for every remaining active occurrence${event.seriesEndDate ? ` through ${formatDate(event.seriesEndDate)}` : ''}.`,
    };
  return {
    label: 'One occurrence',
    detail: `This ticket covers only ${formatWhen(event.start, { year: true })}. Other dates are purchased separately.`,
  };
}

function TicketPurchaseSummary({ event, quantity = 1, compact = false }) {
  const coverage = coverageCopy(event);
  const count = Math.max(1, Number(quantity) || 1);
  const multiply = (value) => (Number.isFinite(Number(value)) ? Number(value) * count : null);
  const ticketTotal = multiply(event.priceMinor);
  const platformTotal = multiply(event.platformFeeMinor);
  const processingTotal = multiply(event.processingFeeMinor);
  const payableTotal = multiply(event.totalPriceMinor ?? event.priceMinor);
  return (
    <div className={`public-ticket-summary${compact ? ' public-ticket-summary--compact' : ''}`}>
      <div className="public-ticket-summary__coverage">
        <strong>{coverage.label}</strong>
        <span>{coverage.detail}</span>
      </div>
      <dl className="public-ticket-summary__price">
        <div>
          <dt>Ticket{count > 1 ? ` × ${count}` : ''}</dt>
          <dd>{formatMoney(ticketTotal, event.currency)}</dd>
        </div>
        {!compact && platformTotal > 0 && (
          <div>
            <dt>Member Nest fee</dt>
            <dd>{formatMoney(platformTotal, event.currency)}</dd>
          </div>
        )}
        {!compact && processingTotal > 0 && (
          <div>
            <dt>Card processing fee</dt>
            <dd>{formatMoney(processingTotal, event.currency)}</dd>
          </div>
        )}
        <div className="public-ticket-summary__total">
          <dt>Total payable</dt>
          <dd>{formatMoney(payableTotal, event.currency)}</dd>
        </div>
      </dl>
      {!compact && count > 1 && <small>Fees and total shown for all {count} attendees.</small>}
    </div>
  );
}

function saleLabel(status) {
  return (
    {
      open: 'Tickets available',
      sold_out: 'Sold out',
      not_open: 'Sales open soon',
      closed: 'Sales closed',
      cancelled: 'Cancelled',
    }[status] || 'Unavailable'
  );
}

function emptyGuest(safety) {
  return {
    name: '',
    phone: '',
    dob: '',
    emergencyContacts: safety ? [{ name: '', relationship: '', phone: '' }] : [],
  };
}

function ProfileRequirements({ missing = [], returnPath }) {
  if (missing.length === 0) return null;
  const profileMissing = missing.filter((field) => field.tab === 'profile');
  const emergencyMissing = missing.some((field) => field.tab === 'emergency');
  const next = encodeURIComponent(returnPath);
  return (
    <div className="public-roster__requirements" role="alert">
      <strong>Complete your saved details before checkout</strong>
      <p>Your profile is missing: {missing.map((field) => field.label).join(', ')}.</p>
      <p>Save the missing information, return to this event, then continue with checkout.</p>
      <div className="public-roster__requirement-actions">
        {profileMissing.length > 0 && (
          <Link className="button button-primary" to={`/profile?next=${next}&reason=event-ticket`}>
            Update my profile
          </Link>
        )}
        {emergencyMissing && (
          <Link className="button button-secondary" to={`/profile?tab=emergency&next=${next}&reason=event-ticket`}>
            Add emergency contact
          </Link>
        )}
      </div>
    </div>
  );
}

function EventBookingChoices({ event, ticketTypeId, setTicketTypeId, bookingAnswers, setBookingAnswers, extras, setExtras }) {
  const bookingFields = (event.bookingForm?.fields || []).filter((field) => field.scope === 'booking');
  return (
    <>
      {(event.ticketTypes || []).length > 0 && (
        <label>
          Ticket type
          <select className="input" value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)}>
            {event.ticketTypes.map((ticket) => (
              <option key={ticket.id} value={ticket.id}>
                {ticket.name} · {formatMoney(ticket.priceMinor, event.currency)}
              </option>
            ))}
          </select>
        </label>
      )}
      {(event.ticketTypes || []).find((ticket) => ticket.id === ticketTypeId)?.nextPrice?.startsAt && <p className="text-muted small">The next price starts {formatWhen(event.ticketTypes.find((ticket) => ticket.id === ticketTypeId).nextPrice.startsAt, { year: true })}.</p>}
      {bookingFields.map((field) => (
        <BookingField key={field.key} field={field} value={bookingAnswers[field.key]} onChange={(value) => setBookingAnswers((values) => ({ ...values, [field.key]: value }))} />
      ))}
      {(event.extras || [])
        .filter((extra) => extra.scope === 'booking')
        .map((extra) => (
          <label key={extra.id}>
            {extra.name} · {formatMoney(extra.priceMinor, event.currency)}
            <input
              className="input"
              type="number"
              min="0"
              max={extra.maxQuantity}
              value={extras[extra.id] || 0}
              onChange={(e) =>
                setExtras((values) => ({
                  ...values,
                  [extra.id]: Number(e.target.value),
                }))
              }
            />
          </label>
        ))}
    </>
  );
}

function AttendeeRoster({ event, onCancel, onCheckout, busy, error, profileStatus, returnPath }) {
  const safety = event.externalInfoPreset === 'safety';
  const [buyerAttending, setBuyerAttending] = useState(true);
  const [guests, setGuests] = useState([]);
  const [ticketTypeId, setTicketTypeId] = useState(event.ticketTypes?.[0]?.id || '');
  const [bookingAnswers, setBookingAnswers] = useState({});
  const [extras, setExtras] = useState({});
  const [purchaserAnswers, setPurchaserAnswers] = useState({});
  const [saveProfileFields, setSaveProfileFields] = useState(false);
  const addGuest = () => setGuests((current) => [...current, emptyGuest(safety)]);
  const updateGuest = (index, patch) => setGuests((current) => current.map((guest, i) => (i === index ? { ...guest, ...patch } : guest)));
  const updateEmergency = (index, patch) =>
    setGuests((current) =>
      current.map((guest, i) =>
        i === index
          ? {
              ...guest,
              emergencyContacts: [{ ...(guest.emergencyContacts?.[0] || {}), ...patch }],
            }
          : guest,
      ),
    );
  const roster = [buyerAttending ? { isPurchaser: true } : null, ...guests].filter(Boolean);

  return (
    <div className="public-roster" role="dialog" aria-modal="true" aria-label="Who is attending">
      <h3>Who is attending?</h3>
      <p className="text-muted">The organiser requires {safety ? 'a safety profile' : 'contact details'} for every attendee.</p>
      <TicketPurchaseSummary event={event} quantity={roster.length || 1} />
      <EventBookingChoices event={event} ticketTypeId={ticketTypeId} setTicketTypeId={setTicketTypeId} bookingAnswers={bookingAnswers} setBookingAnswers={setBookingAnswers} extras={extras} setExtras={setExtras} />
      <label className="public-roster__buyer">
        <input type="checkbox" checked={buyerAttending} onChange={(e) => setBuyerAttending(e.target.checked)} />
        I’m attending
      </label>
      {buyerAttending && <p className="text-muted small">Your saved Member Nest profile will be used for your attendee details.</p>}
      {buyerAttending && profileStatus.loading && (
        <p className="text-muted" role="status">
          Checking your saved details…
        </p>
      )}
      {buyerAttending && <ProfileRequirements missing={profileStatus.missing} returnPath={returnPath} />}
      {buyerAttending &&
        (event.bookingForm?.fields || [])
          .filter((field) => field.scope === 'attendee')
          .map((field) => (
            <BookingField
              key={field.key}
              field={field}
              value={purchaserAnswers[field.key]}
              onChange={(value) =>
                setPurchaserAnswers((values) => ({
                  ...values,
                  [field.key]: value,
                }))
              }
            />
          ))}
      {guests.map((guest, index) => (
        <fieldset key={index} className="public-roster__guest">
          <legend>Guest {index + 1}</legend>
          <label>
            Full name
            <input className="input" value={guest.name} onChange={(e) => updateGuest(index, { name: e.target.value })} required />
          </label>
          <label>
            Contact phone
            <input className="input" type="tel" value={guest.phone} onChange={(e) => updateGuest(index, { phone: e.target.value })} required />
          </label>
          {safety && (
            <>
              <label>
                Date of birth
                <input className="input" type="date" value={guest.dob} onChange={(e) => updateGuest(index, { dob: e.target.value })} required />
              </label>
              <div className="public-roster__emergency">
                <strong>Emergency contact</strong>
                <label>
                  Name
                  <input className="input" value={guest.emergencyContacts?.[0]?.name || ''} onChange={(e) => updateEmergency(index, { name: e.target.value })} required />
                </label>
                <label>
                  Relationship
                  <input className="input" value={guest.emergencyContacts?.[0]?.relationship || ''} onChange={(e) => updateEmergency(index, { relationship: e.target.value })} required />
                </label>
                <label>
                  Phone
                  <input className="input" type="tel" value={guest.emergencyContacts?.[0]?.phone || ''} onChange={(e) => updateEmergency(index, { phone: e.target.value })} required />
                </label>
              </div>
            </>
          )}
          {(event.bookingForm?.fields || [])
            .filter((field) => field.scope === 'attendee')
            .map((field) => (
              <BookingField
                key={field.key}
                field={field}
                value={guest.answers?.[field.key]}
                onChange={(value) =>
                  updateGuest(index, {
                    answers: { ...(guest.answers || {}), [field.key]: value },
                  })
                }
              />
            ))}
          <button type="button" className="button button-ghost" onClick={() => setGuests((current) => current.filter((_, i) => i !== index))}>
            Remove guest
          </button>
        </fieldset>
      ))}
      <button type="button" className="button button-secondary" onClick={addGuest}>
        Add named guest
      </button>
      {(event.bookingForm?.fields || []).some((field) => field.profileBinding && !field.sensitive) && (
        <label className="checkbox-row">
          <input type="checkbox" checked={saveProfileFields} onChange={(e) => setSaveProfileFields(e.target.checked)} /> Save eligible renewable changes to my profile
        </label>
      )}
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      <div className="public-roster__actions">
        <button type="button" className="button button-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={busy || profileStatus.loading || (buyerAttending && profileStatus.missing.length > 0) || roster.length === 0}
          onClick={() =>
            onCheckout(
              roster.map((row) => (row.isPurchaser ? { ...row, answers: purchaserAnswers } : { ...row, answers: row.answers || {} })),
              event,
              {
                ticketTypeId,
                selectedExtras: Object.entries(extras).map(([id, quantity]) => ({ id, quantity })),
                bookingAnswers,
                purchaserAnswers,
                saveProfileFields,
              },
            )
          }
        >
          {busy ? 'Starting checkout…' : `Continue with ${roster.length} ticket${roster.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

function CheckoutConfirmation({ event, busy, error, profileStatus, returnPath, onCancel, onCheckout }) {
  const [ticketTypeId, setTicketTypeId] = useState(event.ticketTypes?.[0]?.id || '');
  const [bookingAnswers, setBookingAnswers] = useState({});
  const [attendeeAnswers, setAttendeeAnswers] = useState({});
  const [extras, setExtras] = useState({});
  const [saveProfileFields, setSaveProfileFields] = useState(false);
  return (
    <div className="public-roster public-checkout-confirmation" role="dialog" aria-modal="true" aria-label="Confirm ticket purchase">
      <h3>Confirm your ticket</h3>
      <p className="text-muted">Check exactly what this ticket covers and what you’ll pay before continuing.</p>
      <TicketPurchaseSummary event={event} />
      <EventBookingChoices event={event} ticketTypeId={ticketTypeId} setTicketTypeId={setTicketTypeId} bookingAnswers={bookingAnswers} setBookingAnswers={setBookingAnswers} extras={extras} setExtras={setExtras} />
      {(event.bookingForm?.fields || [])
        .filter((field) => field.scope === 'attendee')
        .map((field) => (
          <BookingField
            key={field.key}
            field={field}
            value={attendeeAnswers[field.key]}
            onChange={(value) =>
              setAttendeeAnswers((values) => ({
                ...values,
                [field.key]: value,
              }))
            }
          />
        ))}
      {(event.bookingForm?.fields || []).some((field) => field.profileBinding && !field.sensitive) && (
        <label className="checkbox-row">
          <input type="checkbox" checked={saveProfileFields} onChange={(e) => setSaveProfileFields(e.target.checked)} /> Save eligible renewable changes to my profile
        </label>
      )}
      {profileStatus.loading ? (
        <p className="text-muted" role="status">
          Checking your saved details…
        </p>
      ) : (
        <ProfileRequirements missing={profileStatus.missing} returnPath={returnPath} />
      )}
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      <div className="public-roster__actions">
        <button type="button" className="button button-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="button button-primary"
          disabled={busy || profileStatus.loading || profileStatus.missing.length > 0}
          onClick={() =>
            onCheckout([{ isPurchaser: true, answers: attendeeAnswers }], event, {
              ticketTypeId,
              selectedExtras: Object.entries(extras).map(([id, quantity]) => ({ id, quantity })),
              bookingAnswers,
              purchaserAnswers: attendeeAnswers,
              saveProfileFields,
            })
          }
        >
          {busy ? 'Starting checkout…' : 'Continue to secure checkout'}
        </button>
      </div>
    </div>
  );
}

function RosterManager({ booking, busy, error, onClose, onSave }) {
  const [attendees, setAttendees] = useState(booking.attendees || []);
  const update = (index, patch) => setAttendees((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const updateEmergency = (index, patch) =>
    setAttendees((current) =>
      current.map((row, i) =>
        i === index
          ? {
              ...row,
              emergencyContacts: [{ ...(row.emergencyContacts?.[0] || {}), ...patch }],
            }
          : row,
      ),
    );
  return (
    <div className="public-roster" role="dialog" aria-modal="true" aria-label="Manage attendee details">
      <h3>Manage attendee details</h3>
      {booking.identityLocked && <p className="text-muted">Attendee names and dates of birth are locked because the event has started. Contact and emergency details can still be updated.</p>}
      {attendees.map((attendee, index) => (
        <fieldset key={index} className="public-roster__guest">
          <legend>{attendee.accountHolder ? 'Account holder' : `Guest ${index + 1}`}</legend>
          <label>
            Full name
            <input className="input" value={attendee.name || ''} disabled={booking.identityLocked || attendee.accountHolder} onChange={(e) => update(index, { name: e.target.value })} />
          </label>
          <label>
            Contact phone
            <input className="input" type="tel" value={attendee.phone || ''} onChange={(e) => update(index, { phone: e.target.value })} />
          </label>
          {booking.preset === 'safety' && (
            <>
              <label>
                Date of birth
                <input className="input" type="date" value={attendee.dob || ''} disabled={booking.identityLocked || attendee.accountHolder} onChange={(e) => update(index, { dob: e.target.value })} />
              </label>
              <div className="public-roster__emergency">
                <strong>Emergency contact</strong>
                <label>
                  Name
                  <input className="input" value={attendee.emergencyContacts?.[0]?.name || ''} onChange={(e) => updateEmergency(index, { name: e.target.value })} />
                </label>
                <label>
                  Relationship
                  <input className="input" value={attendee.emergencyContacts?.[0]?.relationship || ''} onChange={(e) => updateEmergency(index, { relationship: e.target.value })} />
                </label>
                <label>
                  Phone
                  <input className="input" value={attendee.emergencyContacts?.[0]?.phone || ''} onChange={(e) => updateEmergency(index, { phone: e.target.value })} />
                </label>
              </div>
            </>
          )}
        </fieldset>
      ))}
      {error && <div className="error-message">{error}</div>}
      <div className="public-roster__actions">
        <button type="button" className="button button-ghost" onClick={onClose}>
          Close
        </button>
        <button type="button" className="button button-primary" disabled={busy} onClick={() => onSave(attendees)}>
          {busy ? 'Saving…' : 'Save attendee details'}
        </button>
      </div>
    </div>
  );
}

function PublicWaitlistDialog({ event, busy, error, onCancel, onJoin }) {
  const [quantity, setQuantity] = useState(1);
  const groups = event.externalAttendeeMode === 'buyer_managed_guests';
  return (
    <div className="public-roster" role="dialog" aria-modal="true" aria-label="Join event waitlist">
      <h3>Join the waitlist</h3>
      <p className="text-muted">Places are offered in queue order. Your whole request must fit before it can be offered.</p>
      {groups && (
        <label>
          Places requested
          <input className="input" type="number" min="1" max="20" value={quantity} onChange={(e) => setQuantity(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} />
        </label>
      )}
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      <div className="public-roster__actions">
        <button type="button" className="button button-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="button button-primary" disabled={busy} onClick={() => onJoin(groups ? quantity : 1)}>
          {busy ? 'Joining…' : 'Join waitlist'}
        </button>
      </div>
    </div>
  );
}

function EventCard({ event, clubSlug, user, onBuy, onManage, onWaitlist, onWithdraw }) {
  const href = `/events/${clubSlug}/${event.eventSlug}?occurrence=${encodeURIComponent(event.occurrenceId)}`;
  const currentPath = `${href}`;
  const canBuy = event.saleStatus === 'open' && !event.booked;
  return (
    <article className={`public-event-card ${event.booked ? 'is-booked' : ''}`}>
      <div className="public-event-card__date">
        <time dateTime={event.start}>{formatWhen(event.start, { year: true })}</time>
      </div>
      <div className="public-event-card__body">
        <div className="public-event-card__heading">
          <h2>
            <Link to={href}>{event.name}</Link>
          </h2>
          {event.booked && <span className="public-event-card__booked">Booked</span>}
        </div>
        {event.publicDescription && <p>{event.publicDescription}</p>}
        <div className="public-event-card__meta">
          <span>{event.location?.label}</span>
          <span>
            {saleLabel(event.saleStatus)}
            {event.capacityRemaining != null ? ` · ${event.capacityRemaining} remaining` : ''}
          </span>
        </div>
        <TicketPurchaseSummary event={event} compact />
        {(event.documents || []).length > 0 && (
          <div>
            <strong>Event documents</strong>
            <ul>
              {event.documents.map((document) => (
                <li key={document.id}>
                  <button
                    type="button"
                    className="button-link"
                    onClick={async () => {
                      const result = await getLinkedDocumentFn({
                        clubSlug,
                        eventSlug: event.eventSlug,
                        documentId: document.id,
                      });
                      window.location.assign(result.data.url);
                    }}
                  >
                    {document.title}
                  </button>
                  {document.category ? ` · ${document.category}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {event.booked && event.bookingDetails && (
          <div className="public-event-card__joining">
            <strong>Your joining details</strong>
            {event.bookingDetails.mode === 'online' ? (
              event.bookingDetails.onlineUrl ? (
                <a href={event.bookingDetails.onlineUrl} target="_blank" rel="noreferrer">
                  Open online event
                </a>
              ) : (
                <span>Joining link to follow.</span>
              )
            ) : (
              <span>{[event.bookingDetails.name, event.bookingDetails.street, event.bookingDetails.city, event.bookingDetails.postcode, event.bookingDetails.country].filter(Boolean).join(', ')}</span>
            )}
          </div>
        )}
        {event.booked && (
          <div className="public-event-card__actions">
            {event.ticketWalletPath && (
              <Link className="button button-primary" to={event.ticketWalletPath}>
                View admission tickets
              </Link>
            )}
            <button type="button" className="button button-secondary" onClick={() => onManage(event)}>
              Manage attendee details
            </button>
          </div>
        )}
        {!event.booked && (
          <div className="public-event-card__actions">
            {!user ? (
              <>
                <Link className="button button-primary" to={`/login?next=${encodeURIComponent(currentPath)}`}>
                  {event.waitlistAvailable ? 'Log in to join waitlist' : 'Log in to buy'}
                </Link>
                <Link className="button button-secondary" to={`/register?next=${encodeURIComponent(currentPath)}`}>
                  Create account
                </Link>
              </>
            ) : event.myWaitlist?.status === 'offered' ? (
              <button type="button" className="button button-primary" onClick={() => onBuy(event)}>
                Claim {event.myWaitlist.quantity > 1 ? `${event.myWaitlist.quantity} places` : 'place'}
              </button>
            ) : event.myWaitlist?.status === 'queued' ? (
              <>
                <span className="public-event-card__waitlist-status">
                  You’re on the waitlist · {event.myWaitlist.quantity} {event.myWaitlist.quantity === 1 ? 'place' : 'places'}
                </span>
                <button type="button" className="button button-secondary" onClick={() => onWithdraw(event)}>
                  Leave waitlist
                </button>
              </>
            ) : event.waitlistAvailable ? (
              <button type="button" className="button button-primary" onClick={() => onWaitlist(event)}>
                Join waitlist
              </button>
            ) : (
              <button type="button" className="button button-primary" disabled={!canBuy} onClick={() => onBuy(event)}>
                {canBuy ? (event.ticketPaymentBasis === 'whole_series' ? 'Buy series pass' : event.recurring ? 'Buy this occurrence' : 'Buy ticket') : saleLabel(event.saleStatus)}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function CourseCard({ course, user, clubSlug, onBook, onWaitlist, onPendingPayment, paymentBusy = false, focused = false, compact = false }) {
  const sessions = (course.sessions || []).filter((session) => session.start);
  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  const prices = (course.ticketTypes || []).map((ticket) => Number(ticket.priceMinor || 0));
  const lowest = prices.length ? Math.min(...prices) : null;
  const nextDeadline = (course.ticketTypes || []).map((ticket) => ticket.nextPrice?.startsAt).filter(Boolean).sort()[0];
  const path = `/events/${clubSlug}?view=courses&course=${encodeURIComponent(course.slug)}`;
  return (
    <article id={compact ? undefined : `public-course-${course.slug}`} tabIndex={compact ? undefined : '-1'} className={`public-course-card${compact ? ' public-course-card--compact' : ''}${course.booked ? ' is-booked' : ''}${focused ? ' is-focused' : ''}`}>
      <header className="public-course-card__heading">
        <div>
          <span className="public-course-card__eyebrow">Course</span>
          <h2>{course.name}</h2>
          <p className="public-course-card__range">
            {first
              ? `${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'} · ${formatCourseDate(first.start, { weekday: true, year: true })}${last && last.id !== first.id ? ` to ${formatCourseDate(last.start, { weekday: true, year: true })}` : ''}`
              : 'Course dates to follow'}
          </p>
        </div>
        {course.booked ? <span className="public-course-card__status">Booked</span> : <span className="public-course-card__status">{sessions.length} sessions</span>}
      </header>
      <div className="public-course-card__body">
        {course.description && <p>{course.description}</p>}
        <div className="public-course-card__meta">
          <span>{course.audience === 'members' ? 'Members' : course.audience === 'non_members' ? 'Non-members' : 'Members and non-members'}</span>
          <span>{lowest == null ? 'Price unavailable' : `From ${formatMoney(lowest, course.currency)}`}</span>
          <span>{course.capacityRemaining} remaining</span>
        </div>
        {nextDeadline && <p className="text-muted small">Next price change {formatWhen(nextDeadline, { year: true })}.</p>}
        {!compact && <section className="public-course-card__schedule" aria-label={`${course.name} course dates`}>
          <h3>Course dates</h3>
          {sessions.length ? <ol>
            {sessions.map((session) => (
              <li key={session.id}>
                <time dateTime={session.start}>
                  <strong>{formatCourseDate(session.start, { weekday: true })}</strong>
                  <span>{formatCourseTime(session.start)}{session.end ? `–${formatCourseTime(session.end)}` : ''}</span>
                </time>
                <div>
                  <strong>{session.name || 'Course session'}</strong>
                  {course.booked && (session.resources || []).map((resource, resourceIndex) => (
                    <small className="text-muted" key={`${resource.boatName}-${resourceIndex}`} style={{ display: 'block', marginTop: 4 }}>
                      {resource.boatName} · Responsible: {resource.responsibleMemberName}{resource.provisional ? ' · Provisional' : ''}
                    </small>
                  ))}
                  {session.locationName && <span>{session.locationName}</span>}
                </div>
              </li>
            ))}
          </ol> : <p className="text-muted">Dates will be added soon.</p>}
        </section>}
        {!compact && (course.documents || []).length > 0 && (
          <div className="public-course-card__documents">
            <strong>Course documents</strong>
            <ul>
              {course.documents.map((document) => (
                <li key={document.id}>
                  <button
                    type="button"
                    className="button-link"
                    onClick={async () => {
                      const result = await getLinkedDocumentFn({
                        clubSlug,
                        courseSlug: course.slug,
                        documentId: document.id,
                      });
                      window.location.assign(result.data.url);
                    }}
                  >
                    {document.title}
                  </button>
                  {document.category ? ` · ${document.category}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!compact && course.booked && (course.myBookings || []).length > 0 && (
          <section className="public-course-booking" aria-label={`${course.name} tickets`}>
            <div>
              <span>Your booking</span>
              <h3>Your course tickets</h3>
            </div>
            {(course.myBookings || []).map((booking, bookingIndex) => (
              <article key={`${booking.bookingDate || 'booking'}-${bookingIndex}`}>
                <div className="public-course-booking__meta">
                  <strong>{booking.status === 'partially_refunded' ? 'Partially refunded' : 'Paid'}</strong>
                  {booking.bookingDate && <span>Booked {formatWhen(booking.bookingDate, { year: true })}</span>}
                </div>
                <ul>
                  {(booking.attendees || []).map((attendee, attendeeIndex) => (
                    <li key={`${attendee.name}-${attendee.ticketName}-${attendeeIndex}`}>
                      <span><strong>{attendee.name}</strong><small>{attendee.ticketName}</small></span>
                      <strong>{formatMoney(attendee.priceMinor, booking.currency || course.currency)}</strong>
                    </li>
                  ))}
                </ul>
                {(booking.extras || []).length > 0 && (
                  <p className="text-muted small">
                    Extras: {booking.extras.map((extra) => `${extra.name} × ${extra.quantity}`).join(', ')}
                  </p>
                )}
              </article>
            ))}
          </section>
        )}
        <div className="public-course-card__actions">
          {course.booked || course.paymentPending ? (
            <>
              {course.booked && <span className="public-course-card__status">Booking confirmed</span>}
              {course.booked && course.ticketWalletPath && (
                <Link className="button button-primary" to={course.ticketWalletPath}>
                  View QR tickets
                </Link>
              )}
              {course.paymentPending && <div className="public-course-card__payment-pending">
                <strong>Payment not completed. Your place is not confirmed.</strong>
                <span>Your place is held for up to 10 minutes while you complete checkout.</span>
                <div>
                  <button type="button" className="button button-primary" disabled={paymentBusy} onClick={() => onPendingPayment(course, 'resume')}>
                    {paymentBusy ? 'Checking payment…' : 'Resume payment'}
                  </button>
                  <button type="button" className="button button-secondary" disabled={paymentBusy} onClick={() => onPendingPayment(course, 'cancel')}>
                    Cancel checkout
                  </button>
                </div>
              </div>}
            </>
          ) : !user ? (
            <>
              <Link className="button button-primary" to={`/login?next=${encodeURIComponent(path)}`}>
                Log in to book
              </Link>
              <Link className="button button-secondary" to={`/register?next=${encodeURIComponent(path)}`}>
                Create account
              </Link>
            </>
          ) : !(course.ticketTypes || []).length ? (
            <span>No ticket is available for this account.</span>
          ) : course.myWaitlist?.status === 'offered' || course.capacityRemaining > 0 ? (
            <button type="button" className="button button-primary" onClick={() => onBook(course)}>
              {course.myWaitlist?.status === 'offered' ? 'Claim course place' : 'Book course'}
            </button>
          ) : course.myWaitlist?.status === 'queued' ? (
            <span className="public-event-card__waitlist-status">You’re on the waitlist</span>
          ) : course.waitlistEnabled ? (
            <button type="button" className="button button-secondary" onClick={() => onWaitlist(course)}>
              Join waitlist
            </button>
          ) : (
            <span>Course full</span>
          )}
        </div>
      </div>
    </article>
  );
}

export function buildPublicCalendarEntries(events = [], courses = []) {
  const eventEntries = events
    .filter((event) => event?.start)
    .map((event) => ({
      ...event,
      calendarKind: 'event',
      calendarKey: `event-${event.eventSlug}-${event.occurrenceId || event.start}`,
    }));
  const courseEntries = courses.flatMap((course) =>
    (course.sessions || []).filter((session) => session?.start).map((session) => ({
      calendarKind: 'course',
      calendarKey: `course-${course.slug}-${session.id || session.start}`,
      name: `${course.name} · ${session.name || 'Course session'}`,
      start: session.start,
      end: session.end,
      booked: course.booked,
      course,
    })),
  );
  return [...eventEntries, ...courseEntries].sort((a, b) => new Date(a.start) - new Date(b.start));
}

function BookingField({ field, value, onChange }) {
  const common = {
    className: 'input',
    required: field.required,
    'aria-describedby': field.helpText ? `${field.key}-help` : undefined,
  };
  let control;
  if (field.type === 'long_text') control = <textarea {...common} rows="4" value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  else if (field.type === 'date') control = <input {...common} type="date" value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  else if (field.type === 'number') control = <input {...common} type="number" value={value ?? ''} onChange={(event) => onChange(event.target.value)} />;
  else if (field.type === 'single_choice')
    control = (
      <select {...common} value={value || ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">Choose…</option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  else if (field.type === 'multiple_choice')
    control = (
      <div>
        {field.options.map((option) => (
          <label key={option} className="checkbox-row">
            <input type="checkbox" checked={(value || []).includes(option)} onChange={(event) => onChange(event.target.checked ? [...(value || []), option] : (value || []).filter((item) => item !== option))} /> {option}
          </label>
        ))}
      </div>
    );
  else if (field.type === 'confirmation')
    control = (
      <label className="checkbox-row">
        <input type="checkbox" checked={value === true} required={field.required} onChange={(event) => onChange(event.target.checked)} />
        <span>
          {field.declarationWording || field.label}
          <small> Declaration version {field.declarationVersion}</small>
        </span>
      </label>
    );
  else control = <input {...common} value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  return (
    <div className="form-field">
      <label>
        {field.type === 'confirmation' ? '' : field.label}
        {control}
      </label>
      {field.helpText && (
        <small id={`${field.key}-help`} className="text-muted">
          {field.helpText}
        </small>
      )}
      {field.sensitive && <small className="text-muted">Protected safety information; access is restricted and audited.</small>}
    </div>
  );
}

function CourseBookingDialog({ course, clubSlug, accounts, preferredAccountId = '', onClose, onComplete }) {
  const [attendees, setAttendees] = useState(() => {
    const initialAccount = accounts.find((account) => account.id === preferredAccountId) || accounts[0];
    return initialAccount
      ? [
          {
            accountId: initialAccount.id,
            ticketTypeId: course.ticketTypes?.[0]?.id || '',
            answers: {},
            extras: {},
          },
        ]
      : [];
  });
  const [bookingAnswers, setBookingAnswers] = useState({});
  const [bookingExtras, setBookingExtras] = useState({});
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saveProfileChanges, setSaveProfileChanges] = useState(false);
  useEffect(() => {
    if (accounts.length === 0) return;
    const preferredAccount = accounts.find((account) => account.id === preferredAccountId);
    if (preferredAccount && !attendees.some((attendee) => attendee.accountId === preferredAccount.id)) {
      setAttendees([{
        accountId: preferredAccount.id,
        ticketTypeId: course.ticketTypes?.[0]?.id || '',
        answers: {},
        extras: {},
      }]);
      return;
    }
    if (attendees.length > 0) return;
    const initialAccount = accounts[0];
    setAttendees([{
      accountId: initialAccount.id,
      ticketTypeId: course.ticketTypes?.[0]?.id || '',
      answers: {},
      extras: {},
    }]);
  }, [accounts, attendees, course.ticketTypes, preferredAccountId]);
  useEffect(() => {
    getCourseFormFn({ clubSlug, courseSlug: course.slug })
      .then((result) => {
        const nextForm = result.data?.form || null;
        setForm(nextForm);
        if (!nextForm) return;
        setAttendees((rows) =>
          rows.map((row) => {
            const account = accounts.find((item) => item.id === row.accountId);
            const answers = { ...row.answers };
            nextForm.fields
              .filter((field) => field.scope === 'attendee' && field.profileBinding && !field.sensitive)
              .forEach((field) => {
                if (answers[field.key] == null && account?.profile?.[field.profileBinding] != null) answers[field.key] = account.profile[field.profileBinding];
              });
            return { ...row, answers };
          }),
        );
      })
      .catch(() => setForm(null));
  }, [accounts, clubSlug, course.slug]);
  const addAccount = (accountId) => {
    if (!accountId || attendees.some((row) => row.accountId === accountId)) return;
    setAttendees((rows) => [
      ...rows,
      {
        accountId,
        ticketTypeId: course.ticketTypes?.[0]?.id || '',
        answers: {},
        extras: {},
      },
    ]);
  };
  const update = (index, patch) => setAttendees((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await courseCheckoutFn({
        clubSlug,
        courseSlug: course.slug,
        attendees,
        bookingAnswers,
        bookingExtras,
        saveProfileChanges,
      });
      if (result.data?.url) window.location.assign(result.data.url);
      else onComplete?.();
    } catch (reason) {
      setError(reason?.message || 'Could not create this booking.');
    } finally {
      setBusy(false);
    }
  };
  const available = accounts.filter((account) => !attendees.some((row) => row.accountId === account.id));
  const bookingFields = (form?.fields || []).filter((field) => field.scope === 'booking');
  const attendeeFields = (form?.fields || []).filter((field) => field.scope === 'attendee');
  const childSetupParams = new URLSearchParams({
    view: 'courses',
    course: course.slug,
    bookCourse: '1',
  });
  const childSetupPath = buildChildAccountSetupPath(`/events/${clubSlug}?${childSetupParams.toString()}`);
  return (
    <form className="public-roster" role="dialog" aria-modal="true" aria-label={`Book ${course.name}`} onSubmit={submit}>
      <h2>Book {course.name}</h2>
      <p className="text-muted">One booking covers every listed session. Each attendee must use their own or a parent-managed account.</p>
      <p className="text-muted">
        Booking for a child or another family member? <Link to={childSetupPath}>Set up their parent-managed family account first</Link>. We’ll return here with the new account selected.
      </p>
      {bookingFields.map((field) => (
        <BookingField key={field.key} field={field} value={bookingAnswers[field.key]} onChange={(value) => setBookingAnswers((answers) => ({ ...answers, [field.key]: value }))} />
      ))}
      {attendees.map((attendee, index) => {
        const account = accounts.find((item) => item.id === attendee.accountId);
        return (
          <fieldset key={attendee.accountId} className="public-roster__guest">
            <legend>{account?.name || `Attendee ${index + 1}`}</legend>
            <label>
              Ticket type
              <select className="input" value={attendee.ticketTypeId} onChange={(event) => update(index, { ticketTypeId: event.target.value })}>
                {course.ticketTypes.map((ticket) => (
                  <option key={ticket.id} value={ticket.id}>
                    {ticket.name} · {formatMoney(ticket.priceMinor, course.currency)}
                  </option>
                ))}
              </select>
            </label>
            {attendeeFields.map((field) => (
              <BookingField
                key={field.key}
                field={field}
                value={attendee.answers[field.key]}
                onChange={(value) =>
                  update(index, {
                    answers: { ...attendee.answers, [field.key]: value },
                  })
                }
              />
            ))}
            {course.extras
              .filter((extra) => extra.scope === 'attendee')
              .map((extra) => (
                <label key={extra.id}>
                  {extra.name} · {formatMoney(extra.priceMinor, course.currency)}
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max={extra.maxQuantity}
                    value={attendee.extras[extra.id] || 0}
                    onChange={(event) =>
                      update(index, {
                        extras: {
                          ...attendee.extras,
                          [extra.id]: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
              ))}
            {attendees.length > 1 && (
              <button type="button" className="button button-secondary" onClick={() => setAttendees((rows) => rows.filter((_, i) => i !== index))}>
                Remove attendee
              </button>
            )}
          </fieldset>
        );
      })}
      {available.length > 0 && (
        <label>
          Add another attendee
          <select className="input" value="" onChange={(event) => addAccount(event.target.value)}>
            <option value="">Choose an account…</option>
            {available.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {course.extras
        .filter((extra) => extra.scope === 'booking')
        .map((extra) => (
          <label key={extra.id}>
            {extra.name} · {formatMoney(extra.priceMinor, course.currency)}
            <input
              className="input"
              type="number"
              min="0"
              max={extra.maxQuantity}
              value={bookingExtras[extra.id] || 0}
              onChange={(event) =>
                setBookingExtras((values) => ({
                  ...values,
                  [extra.id]: Number(event.target.value),
                }))
              }
            />
          </label>
        ))}
      {(form?.fields || []).some((field) => field.profileBinding && !field.sensitive) && (
        <label className="checkbox-row">
          <input type="checkbox" checked={saveProfileChanges} onChange={(event) => setSaveProfileChanges(event.target.checked)} /> Save eligible renewable changes back to the selected profiles
        </label>
      )}
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      <div className="public-roster__actions">
        <button type="button" className="button button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="button button-primary" disabled={busy || attendees.length === 0}>
          {busy ? 'Starting checkout…' : 'Continue to checkout'}
        </button>
      </div>
    </form>
  );
}

function MonthCalendar({ entries, month, setMonth, onSelect, showEvents = true, showCourses = true }) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, monthIndex, 1 - mondayOffset);
  const cells = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
  const byDay = entries.reduce((map, entry) => {
    const key = isoDate(new Date(entry.start));
    if (!map[key]) map[key] = [];
    map[key].push(entry);
    return map;
  }, {});
  return (
    <section
      className="public-calendar"
      aria-label={showEvents && showCourses ? 'Events and course dates calendar' : showCourses ? 'Course dates calendar' : 'Events calendar'}
    >
      <div className="public-calendar__toolbar">
        <button type="button" className="button button-secondary" onClick={() => setMonth(new Date(year, monthIndex - 1, 1))}>
          Previous
        </button>
        <h2>
          {month.toLocaleDateString(undefined, {
            month: 'long',
            year: 'numeric',
          })}
        </h2>
        <button type="button" className="button button-secondary" onClick={() => setMonth(new Date(year, monthIndex + 1, 1))}>
          Next
        </button>
      </div>
      <div className="public-calendar__legend" aria-label="Calendar key">
        {showEvents && <span><i />Events</span>}
        {showCourses && <span><i className="is-course" />Course sessions</span>}
      </div>
      <div className="public-calendar__weekdays" aria-hidden="true">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="public-calendar__grid">
        {cells.map((day) => {
          const key = isoDate(day);
          return (
            <div key={key} className={`public-calendar__day ${day.getMonth() !== monthIndex ? 'is-outside' : ''}`}>
              <time dateTime={key}>{day.getDate()}</time>
              {(byDay[key] || []).map((entry) => (
                <button key={entry.calendarKey} type="button" className={`${entry.booked ? 'is-booked ' : ''}${entry.calendarKind === 'course' ? 'is-course' : ''}`.trim()} onClick={() => onSelect(entry)}>
                  <span>
                    {new Date(entry.start).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>{' '}
                  {entry.name}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function PublicEventsPage() {
  const { clubSlug, eventSlug } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const occurrenceFilter = new URLSearchParams(location.search).get('occurrence') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState(() => {
    const requestedView = new URLSearchParams(location.search).get('view');
    return ['calendar', 'courses'].includes(requestedView) ? requestedView : 'list';
  });
  const [month, setMonth] = useState(new Date());
  const [calendarAnchored, setCalendarAnchored] = useState(false);
  const [checkoutEvent, setCheckoutEvent] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [manageBooking, setManageBooking] = useState(null);
  const [profileStatus, setProfileStatus] = useState({
    loading: false,
    missing: [],
  });
  const [waitlistEvent, setWaitlistEvent] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [courseData, setCourseData] = useState({ courses: [] });
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [checkoutCourse, setCheckoutCourse] = useState(null);
  const [pendingCoursePaymentSlug, setPendingCoursePaymentSlug] = useState('');
  const [courseAccounts, setCourseAccounts] = useState([]);
  const courseReturnParams = new URLSearchParams(location.search);
  const requestedCourseSlug = courseReturnParams.get('course') || '';
  const requestedCourseBooking = courseReturnParams.get('bookCourse') === '1';
  const requestedCourseAccountId = courseReturnParams.get('targetUserId') || '';
  const autoOpenedCourseKeyRef = useRef('');
  const [focusedCourseSlug, setFocusedCourseSlug] = useState(requestedCourseSlug);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const start = new Date();
    start.setDate(start.getDate() - 31);
    const end = new Date(start);
    end.setDate(end.getDate() + 370);
    listPublicEventsFn({
      clubSlug,
      eventSlug: eventSlug || undefined,
      start: start.toISOString(),
      end: end.toISOString(),
    })
      .then((result) => {
        if (active) setData(result.data);
      })
      .catch((err) => {
        if (active) setError(err?.message || 'Could not load these public events.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clubSlug, eventSlug, user?.uid, reloadKey]);

  useEffect(() => {
    let active = true;
    setCoursesLoading(true);
    listPublicCoursesFn({ clubSlug })
      .then((result) => {
        if (active) setCourseData(result.data || { courses: [] });
      })
      .catch(() => {
        if (active) setCourseData({ courses: [] });
      })
      .finally(() => {
        if (active) setCoursesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clubSlug, reloadKey]);

  useEffect(() => {
    const nextExpiry = (courseData.courses || [])
      .map((course) => course.paymentPending ? new Date(course.paymentHoldExpiresAt).getTime() : Number.NaN)
      .filter(Number.isFinite)
      .sort((left, right) => left - right)[0];
    if (!nextExpiry) return undefined;
    const delay = Math.max(250, nextExpiry - Date.now() + 500);
    const timer = window.setTimeout(
      () => setReloadKey((key) => key + 1),
      Math.min(delay, 2147483647),
    );
    return () => window.clearTimeout(timer);
  }, [courseData.courses]);

  useEffect(() => {
    if (!requestedCourseBooking || !requestedCourseSlug) return;
    const requestKey = `${requestedCourseSlug}:${requestedCourseAccountId}`;
    if (autoOpenedCourseKeyRef.current === requestKey) return;
    const requestedCourse = (courseData.courses || []).find((course) => course.slug === requestedCourseSlug);
    if (!requestedCourse) return;
    autoOpenedCourseKeyRef.current = requestKey;
    setView('courses');
    setFocusedCourseSlug(requestedCourseSlug);
    setCheckoutCourse(requestedCourse);
  }, [courseData.courses, requestedCourseAccountId, requestedCourseBooking, requestedCourseSlug]);

  useEffect(() => {
    if (!user?.uid || !courseData.courses?.length) {
      setCourseAccounts([]);
      return;
    }
    let active = true;
    (async () => {
      const currentPrivate = await getDoc(doc(db, 'privateUsers', user.uid));
      const currentPublic = await getDoc(doc(db, 'users', user.uid));
      const rows = [
        {
          id: user.uid,
          name: currentPrivate.data()?.displayName || currentPublic.data()?.displayName || user.displayName || user.email || 'My account',
          profile: {
            ...(currentPublic.data() || {}),
            ...(currentPrivate.data() || {}),
          },
        },
      ];
      const familyRoles = currentPublic.data()?.family_roles || {};
      for (const [familyId, role] of Object.entries(familyRoles)) {
        if (role !== 'parent') continue;
        const familyMembers = await getDocs(collection(db, 'families', familyId, 'members'));
        for (const member of familyMembers.docs) {
          if (member.id === user.uid || !['parent', 'child'].includes(member.data()?.role)) continue;
          const [privateProfile, publicProfile] = await Promise.all([getDoc(doc(db, 'privateUsers', member.id)), getDoc(doc(db, 'users', member.id))]);
          rows.push({
            id: member.id,
            name: privateProfile.data()?.displayName || publicProfile.data()?.displayName || member.data()?.name || 'Family account',
            profile: {
              ...(publicProfile.data() || {}),
              ...(privateProfile.data() || {}),
            },
          });
        }
      }
      if (active) setCourseAccounts(Array.from(new Map(rows.map((row) => [row.id, row])).values()));
    })().catch(() => {
      if (active)
        setCourseAccounts([
          {
            id: user.uid,
            name: user.displayName || user.email || 'My account',
          },
        ]);
    });
    return () => {
      active = false;
    };
  }, [courseData.courses, user]);

  const events = useMemo(() => {
    const rows = data?.events || [];
    return occurrenceFilter ? rows.filter((event) => event.occurrenceId === occurrenceFilter) : rows;
  }, [data, occurrenceFilter]);
  const calendarEntries = useMemo(
    () => buildPublicCalendarEntries(events, courseData.courses || []),
    [courseData.courses, events],
  );
  const hasEvents = events.length > 0;
  const hasCourses = (courseData.courses || []).length > 0;
  const activeView = !eventSlug && view === 'courses' && !hasCourses
    ? 'list'
    : !eventSlug && view === 'list' && !hasEvents && hasCourses
      ? 'courses'
      : view;
  useEffect(() => {
    if (activeView !== 'calendar' || calendarAnchored || calendarEntries.length === 0) return;
    const currentMonthHasEntries = calendarEntries.some((entry) => {
      const date = new Date(entry.start);
      return date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth();
    });
    if (!currentMonthHasEntries) {
      const now = new Date();
      const next = calendarEntries.find((entry) => new Date(entry.start) >= now) || calendarEntries[0];
      if (next?.start) {
        const date = new Date(next.start);
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
    setCalendarAnchored(true);
  }, [activeView, calendarAnchored, calendarEntries, month]);
  const pageKind = eventSlug ? 'events' : hasEvents && hasCourses ? 'events and courses' : hasCourses ? 'courses' : 'events';
  const title = eventSlug && events[0] ? `${events[0].name} | ${data?.club?.name || 'Club events'}` : `${data?.club?.name || 'Club'} ${pageKind}`;
  const description = (eventSlug ? events[0]?.publicDescription : '') || data?.club?.description || `Browse dates and book public ${pageKind} from ${data?.club?.name || 'this club'}.`;
  const canonicalPath = eventSlug ? `/events/${clubSlug}/${eventSlug}` : `/events/${clubSlug}`;

  useEffect(() => {
    if (activeView !== 'courses' || !focusedCourseSlug) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(`public-course-${focusedCourseSlug}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeView, focusedCourseSlug]);

  useEffect(() => {
    const id = 'public-event-structured-data';
    document.getElementById(id)?.remove();
    if (!eventSlug || !events[0]) return undefined;
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: events[0].name,
      startDate: events[0].start,
      endDate: events[0].end,
      description,
      url: `https://member-nest.com${canonicalPath}`,
      location:
        events[0].location?.mode === 'online'
          ? { '@type': 'VirtualLocation' }
          : {
              '@type': 'Place',
              address: {
                '@type': 'PostalAddress',
                addressLocality: events[0].location?.city,
                addressCountry: events[0].location?.country,
              },
            },
      offers: {
        '@type': 'Offer',
        price: Number(events[0].totalPriceMinor ?? events[0].priceMinor ?? 0) / 100,
        priceCurrency: events[0].currency,
        availability: events[0].saleStatus === 'sold_out' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      },
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [canonicalPath, description, eventSlug, events]);

  const startCheckout = async (roster, selectedEvent = checkoutEvent, options = {}) => {
    if (!selectedEvent) return;
    setCheckoutBusy(true);
    setCheckoutError('');
    try {
      const result = await checkoutFn({
        checkoutSurface: 'public',
        clubSlug,
        eventSlug: selectedEvent.eventSlug,
        occurrenceId: selectedEvent.ticketPaymentBasis === 'per_occurrence' ? selectedEvent.occurrenceId : '',
        attendeeRoster: roster,
        ticketTypeId: options.ticketTypeId || '',
        selectedExtras: options.selectedExtras || [],
        bookingAnswers: options.bookingAnswers || {},
        attendeeAnswers: user?.uid && options.purchaserAnswers ? { [user.uid]: options.purchaserAnswers } : {},
        saveProfileFields: options.saveProfileFields === true,
        ...(selectedEvent.myWaitlist?.status === 'offered' ? { waitlistEntryId: selectedEvent.myWaitlist.entryId } : {}),
      });
      if (result.data?.completed) {
        setCheckoutEvent(null);
        setReloadKey((key) => key + 1);
        return;
      }
      if (!result.data?.url) throw new Error('Checkout did not return a valid link.');
      window.location.assign(result.data.url);
    } catch (err) {
      const message = err?.message || 'Could not start checkout.';
      setCheckoutError(message.includes('needs a name') || message.includes('phone') || message.includes('emergency') ? `${message} Update your Member Nest profile if your own saved details are incomplete.` : message);
    } finally {
      setCheckoutBusy(false);
    }
  };
  const eventReturnPath = (selectedEvent) => `/events/${clubSlug}/${selectedEvent.eventSlug}?occurrence=${encodeURIComponent(selectedEvent.occurrenceId || '')}`;
  const checkPurchaserProfile = async (selectedEvent) => {
    if (!user?.uid) return [];
    setProfileStatus({ loading: true, missing: [] });
    try {
      const [userSnap, privateSnap] = await Promise.all([getDoc(doc(db, 'users', user.uid)), getDoc(doc(db, 'privateUsers', user.uid))]);
      const missing = getMissingExternalAttendeeProfileFields(userSnap.exists() ? userSnap.data() || {} : {}, privateSnap.exists() ? privateSnap.data() || {} : {}, selectedEvent.externalInfoPreset);
      setProfileStatus({ loading: false, missing });
      return missing;
    } catch {
      setProfileStatus({ loading: false, missing: [] });
      return [];
    }
  };
  const beginPurchase = async (selectedEvent) => {
    setCheckoutEvent(selectedEvent);
    setCheckoutError('');
    await checkPurchaserProfile(selectedEvent);
  };
  const joinWaitlist = async (quantity) => {
    if (!waitlistEvent) return;
    setCheckoutBusy(true);
    setCheckoutError('');
    try {
      await joinEventWaitlistFn({
        clubSlug,
        eventSlug: waitlistEvent.eventSlug,
        occurrenceId: waitlistEvent.occurrenceId,
        quantity,
      });
      setWaitlistEvent(null);
      setReloadKey((key) => key + 1);
    } catch (err) {
      setCheckoutError(err?.message || 'Could not join the waitlist.');
    } finally {
      setCheckoutBusy(false);
    }
  };
  const withdrawWaitlist = async (selectedEvent) => {
    if (!window.confirm('Leave this event waitlist? Your current queue position or offered place may be lost.')) return;

    setCheckoutBusy(true);
    setCheckoutError('');
    try {
      await withdrawEventWaitlistFn({
        clubSlug,
        eventSlug: selectedEvent.eventSlug,
        entryId: selectedEvent.myWaitlist.entryId,
      });
      setReloadKey((key) => key + 1);
    } catch (err) {
      setCheckoutError(err?.message || 'Could not leave the waitlist.');
    } finally {
      setCheckoutBusy(false);
    }
  };
  const joinCourseWaitlist = async (selectedCourse) => {
    try {
      await joinCourseWaitlistFn({
        clubSlug,
        courseSlug: selectedCourse.slug,
      });
      setCheckoutError('You have joined the course waitlist.');
      setReloadKey((key) => key + 1);
    } catch (reason) {
      setCheckoutError(reason?.message || 'Could not join the waitlist.');
    }
  };
  const managePendingCoursePayment = async (selectedCourse, action) => {
    if (action === 'cancel' && !window.confirm(`Cancel the unfinished payment for ${selectedCourse.name}? The held place will be released.`)) return;
    setPendingCoursePaymentSlug(selectedCourse.slug);
    setCheckoutError('');
    try {
      const result = await managePendingCourseCheckoutFn({
        clubSlug,
        courseSlug: selectedCourse.slug,
        action,
      });
      if (result.data?.url) {
        window.location.assign(result.data.url);
        return;
      }
      const state = result.data?.state;
      setCheckoutError(
        state === 'completed'
          ? 'Payment was already completed and your course place is now confirmed.'
          : state === 'cancelled'
            ? 'The unfinished checkout was cancelled and its held place released.'
            : state === 'expired'
              ? 'That checkout had expired. Its held place has been released; you can start a new booking.'
              : 'There is no unfinished checkout to manage.',
      );
      setReloadKey((key) => key + 1);
    } catch (reason) {
      setCheckoutError(reason?.message || `Could not ${action} this course payment.`);
    } finally {
      setPendingCoursePaymentSlug('');
    }
  };
  const openBookingManager = async (selectedEvent) => {
    setCheckoutError('');
    try {
      const result = await getMyBookingFn({
        clubSlug,
        eventSlug: selectedEvent.eventSlug,
        occurrenceId: selectedEvent.occurrenceId,
      });
      if (result.data?.legacy) throw new Error('This older booking does not have an editable attendee roster.');
      setManageBooking({ ...result.data, event: selectedEvent });
    } catch (err) {
      setCheckoutError(err?.message || 'Could not load attendee details.');
    }
  };
  const saveBooking = async (attendees) => {
    if (!manageBooking) return;
    setCheckoutBusy(true);
    setCheckoutError('');
    try {
      await updateMyBookingFn({
        clubSlug,
        eventSlug: manageBooking.event.eventSlug,
        bookingRef: manageBooking.bookingRef,
        attendees,
      });
      setManageBooking(null);
    } catch (err) {
      setCheckoutError(err?.message || 'Could not save attendee details.');
    } finally {
      setCheckoutBusy(false);
    }
  };

  if (loading || (!eventSlug && coursesLoading))
    return (
      <main className="public-events-page">
        <p>Loading public page…</p>
      </main>
    );
  if (error)
    return (
      <main className="public-events-page">
        <h1>Public events</h1>
        <p className="error-message">{error}</p>
      </main>
    );

  return (
    <main
      className="public-events-page"
      style={{ '--public-primary': data?.club?.brand?.primaryColor || '#2447a8' }}
    >
      <SeoMeta page={{ title, description, canonicalPath }} />
      <header className="public-events-hero">
        {data?.club?.brand?.logoUrl && <img src={data.club.brand.logoUrl} alt="" />}
        <div>
          <span>{eventSlug ? 'Public event' : `Public ${pageKind}`}</span>
          <h1>{eventSlug && events[0] ? events[0].name : data?.club?.name}</h1>
          <p>{description}</p>
        </div>
      </header>
      {!eventSlug && (hasEvents || hasCourses) && (
        <div className="public-events-view-toggle" role="group" aria-label="Public booking view">
          {hasEvents && <button type="button" className={activeView === 'list' ? 'is-active' : ''} onClick={() => setView('list')}>
            {hasCourses ? 'Overview' : 'Events'}
          </button>}
          {!hasEvents && hasCourses && <button type="button" className={activeView === 'courses' ? 'is-active' : ''} onClick={() => setView('courses')}>
            Courses
          </button>}
          <button type="button" className={activeView === 'calendar' ? 'is-active' : ''} onClick={() => setView('calendar')}>
            Calendar
          </button>
          {hasEvents && hasCourses && (
            <button type="button" className={activeView === 'courses' ? 'is-active' : ''} onClick={() => setView('courses')}>
              Courses
            </button>
          )}
        </div>
      )}
      {activeView === 'courses' ? (
        courseData.courses?.length ? (
          <section className="public-page-section" aria-labelledby="public-courses-heading">
            <div className="public-page-section__heading">
              <div>
                <span>Whole-course booking</span>
                <h2 id="public-courses-heading">Courses you can book</h2>
                <p>Compare dates, availability and prices, then choose the attendees joining the complete course.</p>
              </div>
              <strong>{courseData.courses.length} {courseData.courses.length === 1 ? 'course' : 'courses'}</strong>
            </div>
            <div className="public-course-list">
              {courseData.courses.map((course) => (
                <CourseCard
                  key={course.slug}
                  course={course}
                  clubSlug={clubSlug}
                  user={user}
                  focused={focusedCourseSlug === course.slug}
                  onBook={setCheckoutCourse}
                  onWaitlist={joinCourseWaitlist}
                  onPendingPayment={managePendingCoursePayment}
                  paymentBusy={pendingCoursePaymentSlug === course.slug}
                />
              ))}
            </div>
          </section>
        ) : (
          <div className="public-page-empty">
            <strong>No courses are currently open for booking.</strong>
            <p>No public courses are currently available.</p>
          </div>
        )
      ) : !eventSlug && activeView === 'calendar' ? (
        calendarEntries.length ? (
          <MonthCalendar
            entries={calendarEntries}
            month={month}
            setMonth={setMonth}
            showEvents={hasEvents}
            showCourses={hasCourses}
            onSelect={(entry) => {
              if (entry.calendarKind === 'course') {
                setFocusedCourseSlug(entry.course.slug);
                setView('courses');
                return;
              }
              window.location.assign(`/events/${clubSlug}/${entry.eventSlug}?occurrence=${entry.occurrenceId}`);
            }}
          />
        ) : (
          <div className="card">
            <p>No public {pageKind} dates are currently available.</p>
          </div>
        )
      ) : eventSlug ? (
        events.length > 0 ? (
          <section className="public-events-list">
            {events.map((event) => (
              <EventCard
                key={`${event.eventSlug}-${event.occurrenceId}`}
                event={event}
                clubSlug={clubSlug}
                user={user}
                onManage={openBookingManager}
                onBuy={beginPurchase}
                onWaitlist={(selected) => {
                  setCheckoutError('');
                  setWaitlistEvent(selected);
                }}
                onWithdraw={withdrawWaitlist}
              />
            ))}
          </section>
        ) : (
          <div className="public-page-empty"><p>This event is not currently available.</p></div>
        )
      ) : (
        <div className="public-overview">
          {events.length > 0 && <section className="public-page-section" aria-labelledby="upcoming-events-heading">
            <div className="public-page-section__heading">
              <div>
                <span>Upcoming</span>
                <h2 id="upcoming-events-heading">Events</h2>
              </div>
              <strong>{events.length} {events.length === 1 ? 'date' : 'dates'}</strong>
            </div>
            <div className="public-events-list">
              {events.map((event) => (
                <EventCard
                  key={`${event.eventSlug}-${event.occurrenceId}`}
                  event={event}
                  clubSlug={clubSlug}
                  user={user}
                  onManage={openBookingManager}
                  onBuy={beginPurchase}
                  onWaitlist={(selected) => {
                    setCheckoutError('');
                    setWaitlistEvent(selected);
                  }}
                  onWithdraw={withdrawWaitlist}
                />
              ))}
            </div>
          </section>}
          {courseData.courses?.length > 0 && <section className="public-page-section" aria-labelledby="upcoming-courses-heading">
            <div className="public-page-section__heading">
              <div>
                <span>Learn and take part</span>
                <h2 id="upcoming-courses-heading">Courses</h2>
                <p>One booking covers every listed session.</p>
              </div>
              <button type="button" className="button button-secondary" onClick={() => setView('courses')}>View all courses</button>
            </div>
            <div className="public-course-list public-course-list--overview">
              {courseData.courses.slice(0, 3).map((course) => (
                <CourseCard
                  key={course.slug}
                  course={course}
                  clubSlug={clubSlug}
                  user={user}
                  compact
                  onBook={setCheckoutCourse}
                  onWaitlist={joinCourseWaitlist}
                  onPendingPayment={managePendingCoursePayment}
                  paymentBusy={pendingCoursePaymentSlug === course.slug}
                />
              ))}
            </div>
          </section>}
          {events.length === 0 && !courseData.courses?.length && (
            <div className="public-page-empty">
              <strong>Nothing is open for booking just now.</strong>
              <p>New public events will appear here when the club publishes them.</p>
            </div>
          )}
        </div>
      )}
      {eventSlug && (
        <Link className="public-events-back" to={`/events/${clubSlug}`}>
          View all {data?.club?.name} events
        </Link>
      )}
      {checkoutEvent && checkoutEvent.externalAttendeeMode === 'buyer_managed_guests' && (
        <div className="public-roster-backdrop" onClick={() => setCheckoutEvent(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <AttendeeRoster event={checkoutEvent} busy={checkoutBusy} error={checkoutError} profileStatus={profileStatus} returnPath={eventReturnPath(checkoutEvent)} onCancel={() => setCheckoutEvent(null)} onCheckout={startCheckout} />
          </div>
        </div>
      )}
      {checkoutEvent && checkoutEvent.externalAttendeeMode === 'individual_accounts' && (
        <div className="public-roster-backdrop" onClick={() => setCheckoutEvent(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <CheckoutConfirmation
              event={checkoutEvent}
              busy={checkoutBusy}
              error={checkoutError}
              profileStatus={profileStatus}
              returnPath={eventReturnPath(checkoutEvent)}
              onCancel={() => {
                setCheckoutError('');
                setCheckoutEvent(null);
              }}
              onCheckout={startCheckout}
            />
          </div>
        </div>
      )}
      {manageBooking && (
        <div className="public-roster-backdrop" onClick={() => setManageBooking(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <RosterManager booking={manageBooking} busy={checkoutBusy} error={checkoutError} onClose={() => setManageBooking(null)} onSave={saveBooking} />
          </div>
        </div>
      )}
      {waitlistEvent && (
        <div className="public-roster-backdrop" onClick={() => setWaitlistEvent(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <PublicWaitlistDialog event={waitlistEvent} busy={checkoutBusy} error={checkoutError} onCancel={() => setWaitlistEvent(null)} onJoin={joinWaitlist} />
          </div>
        </div>
      )}
      {checkoutCourse && (
        <div className="public-roster-backdrop" onClick={() => setCheckoutCourse(null)}>
          <div onClick={(event) => event.stopPropagation()}>
            <CourseBookingDialog
              course={checkoutCourse}
              clubSlug={clubSlug}
              accounts={courseAccounts}
              preferredAccountId={requestedCourseAccountId}
              onClose={() => setCheckoutCourse(null)}
              onComplete={() => {
                setCheckoutCourse(null);
                setReloadKey((key) => key + 1);
              }}
            />
          </div>
        </div>
      )}
      {checkoutError && !checkoutEvent && !manageBooking && (
        <div className="public-checkout-error" role="alert">
          <p>{checkoutError}</p>
          <button type="button" className="button button-secondary" onClick={() => setCheckoutError('')}>
            Close
          </button>
        </div>
      )}
    </main>
  );
}
